import { uomFactors, type ParsedCsv } from "./csv";
import { mappedResultProperties } from "./resultProperties";

/*
 *
 * Nothing looser: fuzzy matching is how a reading lands on the wrong point, and a wrong sample
 * point is a wrong place to dig — the same rule the animations refuse to bend.
 */
export function normalizeId(raw: unknown): string {
  const s = String(raw ?? "")
    .trim()
    .toLowerCase();
  return /^0\d+$/.test(s) ? String(Number(s)) : s;
}

/*
 *
 * The ids a target layer's features carry.
 *
 * ZONES: the SERVER ORDINAL of each zone (see `zoneIdIndex`) — the id SS3c attaches by.
 * File-owned point layers (`id_source: "file"`) match the uploaded `point_id`.
 * Every other point layer still prefers `num` — the human "1".."25" a lab CSV carries.
 */
export function layerFeatureIds(
  fc: GeoJSON.FeatureCollection | null | undefined,
  kind: "points" | "zones",
): string[] {
  if (kind === "zones") return zoneIdIndex(fc).ordinals.map(String);
  const out: string[] = [];
  for (const f of fc?.features ?? []) {
    const p = (f.properties ?? {}) as Record<string, unknown>;
    const raw =
      p.id_source === "file"
        ? (p.point_id ?? p.num ?? p.sample_id ?? p.id)
        : (p.num ?? p.point_id ?? p.sample_id ?? p.id);
    if (raw != null && String(raw).trim() !== "") out.push(normalizeId(raw));
  }
  return out;
}

/*
 *
 * TWO VOCABULARIES, NEVER ONE MAP.
 */
export interface ZoneIdIndex {
  /* * every zone's server ordinal, in feature order */
  ordinals: number[];
  /* * the MERGED view — ordinals overlaid by names — used only when a CSV speaks neither vocabulary cleanly */
  byId: Map<string, number>;
  /* * normalized NAME/code/file-id → ordinal (the vocabulary the user sees) */
  byName: Map<string, number>;
  /* * normalized SERVER ORDINAL → ordinal */
  byOrdinal: Map<string, number>;
  /* * ordinal → the name the user sees for that zone (zone_name, else code, else the on-screen number) */
  display: Map<number, string>;
  /* * ids that two or more zones claim WITHIN one vocabulary, and which therefore resolve to neither */
  ambiguous: string[];
}

type Claim = { id: string; raw: string; ordinal: number };

/* * Claims of one vocabulary → its lookup, with the genuinely contested ids pulled out. */
function ownEach(claims: Claim[]): {
  map: Map<string, number>;
  ambiguous: string[];
} {
  const owner = new Map<string, number | null>(); // null = claimed by more than one zone
  const rawOf = new Map<string, string>(); // normalized id → the spelling first seen (for messages)
  for (const c of claims) {
    if (!rawOf.has(c.id)) rawOf.set(c.id, c.raw);
    if (!owner.has(c.id)) owner.set(c.id, c.ordinal);
    else if (owner.get(c.id) !== c.ordinal) owner.set(c.id, null); // same zone twice is NOT a clash.
  }
  const map = new Map<string, number>();
  const ambiguous: string[] = [];
  for (const [id, o] of owner) {
    if (o == null) ambiguous.push(rawOf.get(id) ?? id);
    else map.set(id, o);
  }
  return { map, ambiguous };
}

export function zoneIdIndex(
  fc: GeoJSON.FeatureCollection | null | undefined,
): ZoneIdIndex {
  const ordinals: number[] = [];
  const display = new Map<number, string>();
  const nameClaims: Claim[] = [];
  const ordClaims: Claim[] = [];
  const feats = fc?.features ?? [];
  feats.forEach((f, i) => {
    const p = (f.properties ?? {}) as Record<string, unknown>;
    // 13 would have keyed the attach to ordinals that do not exist.
    const ordinal = i + 1;
    ordinals.push(ordinal);
    // the on-screen number: the server stamps `zone` = zone_number (Z1b/1H), while an editor-saved
    // layer's `zone` is its cluster label (1,2,3,7 after a split) — the number the map actually prints
    const onScreen = p.zone ?? p.zone_number;
    const text = (v: unknown) =>
      v != null && String(v).trim() !== "" ? String(v) : null;
    display.set(
      ordinal,
      text(p.zone_name) ?? text(p.code) ?? text(onScreen) ?? String(ordinal),
    );
    ordClaims.push({ id: normalizeId(ordinal), raw: String(ordinal), ordinal });
    // `zone`/`zone_number` land in the ORDINAL vocabulary when they ARE the ordinal (a served layer
    // the claim is then idempotent) and in the NAME vocabulary when they differ (an editor
    // cluster label, or a file's own numbering: the number on screen, which is what a lab types
    // back).
    for (const raw of [
      p.zone_name,
      p.code,
      p.source_zone_id,
      p.source_zone_name,
      p.zone,
      p.zone_number,
    ]) {
      const s = text(raw);
      if (s == null) continue;
      const id = normalizeId(s);
      (id === normalizeId(ordinal) ? ordClaims : nameClaims).push({
        id,
        raw: s,
        ordinal,
      });
    }
  });
  const byName = ownEach(nameClaims);
  const byOrdinal = ownEach(ordClaims);
  // the merged fallback: ordinals first, names overlaid — deterministic, and it prefers the id the
  // user can actually see when a CSV speaks neither vocabulary cleanly
  const byId = new Map(byOrdinal.map);
  for (const [id, o] of byName.map) byId.set(id, o);
  return {
    ordinals,
    byId,
    byName: byName.map,
    byOrdinal: byOrdinal.map,
    display,
    ambiguous: [...byName.ambiguous, ...byOrdinal.ambiguous],
  };
}

/*
 *
 * WHICH VOCABULARY IS THIS CSV SPEAKING — decided once, over the WHOLE file.
 *
 * A grower's sheet keys its zones one way, not two: the names the map shows them, or the numbers.
 * Deciding per-id is what produced the collisions this replaced, so the choice is made from the file
 * as a whole: the first vocabulary that places EVERY id wins, names before ordinals because a name is
 * what the user sees and what the export carried to the lab. `byId` is the third candidate so a
 * genuinely mixed sheet ("North", 2, 3) still resolves. If none places everything the file is wrong
 * somewhere, and we keep whichever places the most so the refusal names the fewest ids.
 */
export function zoneLookupFor(
  ids: string[],
  idx: ZoneIdIndex,
): Map<string, number> {
  const candidates = [idx.byName, idx.byOrdinal, idx.byId];
  if (!ids.length) return idx.byId;
  const hits = (m: Map<string, number>) =>
    ids.reduce((n, id) => n + (m.has(id) ? 1 : 0), 0);
  for (const m of candidates) if (hits(m) === ids.length) return m;
  return candidates.reduce((best, m) => (hits(m) > hits(best) ? m : best));
}

/*
 *
 * AN UNPLACEABLE ID MUST NOT BE ABLE TO LOOK LIKE AN ORDINAL.
 */
export const UNPLACED = "?";

/*
 *
 * A CSV's ids as COMPARISON KEYS: the ordinal each resolved to, or the id itself marked unplaceable so
 * `strictIdMatch` reports it as extra — by the string the CSV actually said (`zoneKeyLabel`).
 */
export function resolveZoneCsvIds(ids: string[], idx: ZoneIdIndex): string[] {
  const lut = zoneLookupFor(ids, idx);
  return ids.map((id) => {
    const o = lut.get(id);
    return o == null ? UNPLACED + id : String(o);
  });
}

/*
 *
 * What to SHOW the user for one comparison key. A resolved key is an ordinal, and the user has never
 * seen an ordinal — show the zone's name. An unplaced key is the CSV's own text, and must be echoed
 * back exactly: translating it through `display` is how "13" came back as a duplicate of "12".
 */
export function zoneKeyLabel(
  key: string,
  idx: ZoneIdIndex | null | undefined,
): string {
  if (key.startsWith(UNPLACED)) return key.slice(UNPLACED.length);
  if (!idx) return key;
  const o = Number(key);
  return (Number.isInteger(o) ? idx.display.get(o) : undefined) ?? key;
}

/* * The ids in the parsed CSV's mapped Point-ID column, or null when no column mapped. */
export function csvIds(
  parsed: ParsedCsv,
  mapping: Record<string, number | null>,
): string[] | null {
  const col = mapping.point_id;
  if (col == null) return null;
  return parsed.rows.map((r) => normalizeId(r[col])).filter((v) => v !== "");
}

export type IdMatchVerdict =
  | { ok: true; count: number }
  | {
      ok: false;
      missingInCsv: string[];
      extraInCsv: string[];
      duplicateInCsv: string[];
      duplicateInLayer: string[];
    };

/* * Set equality, with every way it failed named. */
export function strictIdMatch(layer: string[], csv: string[]): IdMatchVerdict {
  const dupsOf = (xs: string[]) => {
    const seen = new Set<string>();
    const dup = new Set<string>();
    for (const x of xs) (seen.has(x) ? dup : seen).add(x);
    return [...dup];
  };
  const duplicateInCsv = dupsOf(csv);
  const duplicateInLayer = dupsOf(layer);
  const layerSet = new Set(layer);
  const csvSet = new Set(csv);
  const missingInCsv = [...layerSet].filter((x) => !csvSet.has(x));
  const extraInCsv = [...csvSet].filter((x) => !layerSet.has(x));
  if (
    !missingInCsv.length &&
    !extraInCsv.length &&
    !duplicateInCsv.length &&
    !duplicateInLayer.length
  ) {
    return { ok: true, count: layerSet.size };
  }
  return {
    ok: false,
    missingInCsv,
    extraInCsv,
    duplicateInCsv,
    duplicateInLayer,
  };
}

/*
 * Point layers allow a point with no row: flooded and unreachable spots are
 * routine. A CSV id that is not on the layer, or a repeated id, is still a
 * refusal — a wrong id is never attached.
 */
export function pointLayerIdMatch(
  layer: string[],
  csv: string[],
): IdMatchVerdict {
  const verdict = strictIdMatch(layer, csv);
  if (verdict.ok) return verdict;
  if (
    verdict.extraInCsv.length === 0 &&
    verdict.duplicateInCsv.length === 0 &&
    verdict.duplicateInLayer.length === 0
  ) {
    return { ok: true, count: new Set(csv).size };
  }
  return { ...verdict, missingInCsv: [] };
}

/* * Layer ids the CSV does not carry, in first-seen order. */
export function layerIdsMissingFromCsv(
  layer: string[],
  csv: string[],
): string[] {
  const have = new Set(csv);
  const seen = new Set<string>();
  const missing: string[] = [];
  for (const id of layer) {
    if (seen.has(id)) continue;
    seen.add(id);
    if (!have.has(id)) missing.push(id);
  }
  return missing;
}

/*
 *
 * They did: the verdict compared unresolved ids against ordinal strings and could pass a file this
 * then refused with "Zone ids must be this layer's zone names or numbers" a green panel and a red
 * toast for one CSV.
 */
export function buildZoneResultRows(
  parsed: ParsedCsv,
  mapping: Record<string, number | null>,
  idx: ZoneIdIndex,
): {
  rows: { zone: number; properties: Record<string, unknown> }[];
  valuelessZones: string[];
  badZoneIds: string[];
} {
  const idCol = mapping.point_id;
  const factors = uomFactors(parsed, mapping);
  const lut = zoneLookupFor(csvIds(parsed, mapping) ?? [], idx);
  const rows: { zone: number; properties: Record<string, unknown> }[] = [];
  const valuelessZones: string[] = [];
  const badZoneIds: string[] = [];
  for (const r of parsed.rows) {
    const rawId = idCol != null ? normalizeId(r[idCol]) : "";
    // the CSV may name a zone by its number OR its name/code — the index says which polygon; an id
    // it cannot place (unknown, or shared by two zones) is a NAMED failure, never a guess
    const ordinal = lut.get(rawId);
    if (ordinal == null) {
      if (rawId !== "") badZoneIds.push(rawId);
      continue;
    }
    const built = mappedResultProperties(r, mapping, factors);
    if (!built.hasValue) {
      valuelessZones.push(idx.display.get(ordinal) ?? rawId);
      continue;
    }
    rows.push({ zone: ordinal, properties: built.properties });
  }
  return { rows, valuelessZones, badZoneIds };
}

/*
 *
 * COUNT ALONE CANNOT DISCRIMINATE: every zone version numbers its zones 1.
 */
export function sameZoneGeometry(
  a: GeoJSON.FeatureCollection | null,
  b: GeoJSON.FeatureCollection | null,
): boolean {
  const fa = a?.features ?? [];
  const fb = b?.features ?? [];
  if (!fa.length || fa.length !== fb.length) return false;
  const first = (f: { geometry?: unknown }): [number, number] | null => {
    let c: unknown = (
      f.geometry as { coordinates?: unknown } | null | undefined
    )?.coordinates;
    while (Array.isArray(c) && Array.isArray(c[0])) c = c[0];
    return Array.isArray(c) &&
      typeof c[0] === "number" &&
      typeof c[1] === "number"
      ? [c[0], c[1]]
      : null;
  };
  for (let i = 0; i < fa.length; i++) {
    const pa = first(fa[i]!);
    const pb = first(fb[i]!);
    if (
      !pa ||
      !pb ||
      Math.abs(pa[0] - pb[0]) > 1e-7 ||
      Math.abs(pa[1] - pb[1]) > 1e-7
    )
      return false;
  }
  return true;
}
